'use client';
/**
 * Parcours d'intégration — de l'arrivée à la prise de poste (lot H4).
 *
 * Décision du RQ (29/07/2026) : les personnes déjà en poste ont été habilitées
 * par ancienneté ; les nouvelles suivent formation → habilitation → prise de
 * poste. Cet écran rend ce parcours visible et actionnable, au lieu de le
 * laisser à la mémoire de celui qui accueille.
 *
 * Deux partis pris :
 *  · le parcours ne s'applique PAS qu'aux nouveaux. Une étape manquante chez
 *    quelqu'un d'ancien est exactement le même trou — masquer les anciens
 *    reviendrait à décider que leurs manques n'en sont pas ;
 *  · la dernière étape n'est pas « a des habilitations » mais « peut tenir au
 *    moins un poste ». C'est la seule qui se vérifie contre la réalité : une
 *    habilitation qui ne correspond à aucun poste ne rend personne planifiable.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import type { RhHabilitationDetenue, RhContrat, RhPoste } from '@/lib/types';
import { I, card, RefISO, EmptyState, Skeleton, enChargement } from '@/components/ui';
import { DossierIntegration } from '@/components/screens/RhIntegration';
import { nomDe } from '@/lib/nom';
import { fonctionAffichee } from '@/lib/fonction-affichee';
import { fmtDate } from '@/lib/meta';
import { Tableau } from '@/shared/Tableau';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
const FORMATION = 'formation';

export interface EtapeParcours {
  cle: 'fiche' | 'badge' | 'contrat' | 'formation' | 'habilitation' | 'poste';
  titre: string;
  fait: boolean;
  /** ce qui manque, dit en clair */
  detail: string;
  /** une étape peut être sautée sans que le parcours soit bloqué */
  facultative?: boolean;
}

/**
 * État du parcours d'une personne. Fonction PURE : c'est elle qu'on relira le
 * jour où un audit demandera « sur quoi vous fondez-vous pour dire que cette
 * personne pouvait tenir ce poste ? ».
 */
export function parcoursDe(
  personne: { orgId: string; badgeId?: string | null; dateEntree?: string | null },
  contrats: RhContrat[],
  detenues: RhHabilitationDetenue[],
  postes: RhPoste[],
): EtapeParcours[] {
  const mes = detenues.filter((d) => d.orgId === personne.orgId);
  const acquises = mes.filter((d) => d.niveau !== FORMATION);
  const enFormation = mes.filter((d) => d.niveau === FORMATION);
  const contrat = contrats.find((c) => c.personnelOrgId === personne.orgId && c.actif !== false);

  // Un poste est tenable si TOUTES ses exigences sont couvertes par une
  // habilitation acquise. Les exigences par discipline (ancien modèle) ne sont
  // pas évaluables ici : on ne les compte pas comme couvertes, mais on ne les
  // compte pas non plus comme un échec — c'est le serveur qui tranche.
  const codesAcquis = new Set(acquises.map((d) => d.code));
  const tenables = postes.filter((p) => {
    const ex = (p.habilitationsRequises || []).filter((e) => typeof e !== 'string') as { code: string }[];
    return ex.length > 0 && ex.every((e) => codesAcquis.has(e.code));
  });

  return [
    {
      cle: 'fiche', titre: 'Fiche créée', fait: true,
      detail: personne.dateEntree ? `Entrée le ${personne.dateEntree}` : 'Date d’entrée non renseignée',
    },
    {
      cle: 'badge', titre: 'Badge pointeuse', fait: !!personne.badgeId,
      detail: personne.badgeId
        ? `Badge ${personne.badgeId}`
        : 'Sans badge, ses pointages resteront orphelins — la badgeuse n’émet qu’un numéro.',
    },
    {
      cle: 'contrat', titre: 'Contrat actif', fait: !!contrat,
      detail: contrat
        ? `${contrat.contratType.toUpperCase()}${contrat.joursCongesAnnuels ? ` · ${contrat.joursCongesAnnuels} j de congé` : ' · droit à congé non renseigné'}`
        : 'Sans contrat, aucun solde de congé ne peut être calculé pour cette personne.',
    },
    {
      cle: 'formation', titre: 'Formation engagée', fait: !!enFormation.length || !!acquises.length,
      facultative: true,
      detail: enFormation.length
        ? `${enFormation.length} habilitation(s) au niveau « en formation »`
        : acquises.length ? 'Étape franchie' : 'Aucune formation enregistrée.',
    },
    {
      cle: 'habilitation', titre: 'Habilitation acquise', fait: !!acquises.length,
      detail: acquises.length
        ? `${acquises.length} habilitation(s) acquise(s)`
        : 'En formation ne vaut pas habilitation : cette personne ne peut tenir aucun poste qui en exige une.',
    },
    {
      cle: 'poste', titre: 'Peut tenir un poste', fait: !!tenables.length,
      detail: tenables.length
        ? `${tenables.length} poste(s) : ${tenables.slice(0, 3).map((p) => p.libelle).join(', ')}${tenables.length > 3 ? '…' : ''}`
        : 'Ses habilitations ne couvrent entièrement aucun poste déclaré.',
    },
  ];
}

/** Ce qu'une case du tableau dit d'un point du parcours : fait, à faire, ou facultatif non fait. */
export type EtatCellule = 'fait' | 'manque' | 'facultatif';
export function etatCellule(e: Pick<EtapeParcours, 'fait' | 'facultative'>): EtatCellule {
  return e.fait ? 'fait' : e.facultative ? 'facultatif' : 'manque';
}
const LIBELLE_CELLULE: Record<EtatCellule, string> = { fait: 'fait', manque: 'à faire', facultatif: 'facultatif, non fait' };

/* -------------------------------------------------------------------------- */

export function ParcoursIntegration({ onAllerHabilitations }: { onAllerHabilitations?: (orgId: string) => void }) {
  const { rhPersonnel, rhContrats, rhDetenues, rhPostes, rhIntegrations, ouvrirIntegration, loading, booted, settings } = useGed();
  const orgFonctions = settings?.orgFonctions || [];
  /* ⚠️ LE FAUX VIDE LE PLUS TROMPEUR DU LOT (BLOQ-03), parce qu'il RASSURE :
     « Aucun parcours à compléter · chaque personne de l'effectif a son badge,
     son contrat, ses habilitations ». Servie avant l'arrivée des données, cette
     phrase affirme qu'un dossier RH est complet alors qu'on n'en a lu aucun.
     Les CINQ collections comptent : le parcours se dit incomplet par ce qui
     MANQUE, donc une seule collection absente suffit à faire croire que tout
     va bien. */
  const attend = enChargement(booted, loading,
    rhPersonnel.length, rhContrats.length, rhDetenues.length, rhPostes.length, rhIntegrations.length);
  const [tout, setTout] = React.useState(false);
  /* LA PERSONNE EN LIGNE, LES POINTS EN COLONNES (RQ, 03/10/2026) : c'est la vue qui répond
     à « qui n'a pas encore son badge, son contrat… ? » d'un seul regard. Les cartes restent
     pour le détail (le « pourquoi » de chaque point est écrit en toutes lettres). Le choix
     est mémorisé dans ce navigateur ; illisible, on retombe sur le tableau. */
  const [vue, setVueEtat] = React.useState<'tableau' | 'cartes'>('tableau');
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- lecture du navigateur au montage (pas lisible au rendu serveur)
    try { if (window.localStorage.getItem('bio24.parcours.vue') === 'cartes') setVueEtat('cartes'); } catch { /* sans mémoire */ }
  }, []);
  const setVue = (v: 'tableau' | 'cartes') => {
    setVueEtat(v);
    try { window.localStorage.setItem('bio24.parcours.vue', v); } catch { /* sans mémoire */ }
  };
  /** Dossier ouvert en plein écran : 51 activités ne tiennent pas dans un tiroir. */
  const [dossierOuvert, setDossierOuvert] = React.useState<string | null>(null);

  const dossier = rhIntegrations.find((d) => d.id === dossierOuvert);
  /* ⚠️ `onAllerHabilitations` DESCEND JUSQU'AU DOSSIER (12/09/2026, volet ①).
     Cette sortie anticipée ne le transmettait pas : le dossier ouvert était donc
     coupé du seul chemin qui marche, et son bouton « Ouvrir les dossiers
     d'habilitation » retombait sur `naviguerVers('rhhab')` — l'écran sur lequel
     on se trouve DÉJÀ. L'onglet est un `useState` initialisé une seule fois :
     rien ne changeait à l'écran, et le bouton paraissait mort.
     Le rappel, lui, bascule l'onglet ET pré-remplit la personne. Il existait
     depuis le début ; il s'arrêtait simplement une ligne trop tôt. */
  if (dossier) {
    return (
      <DossierIntegration dossier={dossier} onFermer={() => setDossierOuvert(null)}
        onAllerHabilitations={onAllerHabilitations} />
    );
  }

  const gens = rhPersonnel
    .filter((p) => p.statut !== 'sorti' && p.type !== 'externe')
    .map((p) => ({ p, etapes: parcoursDe(p, rhContrats, rhDetenues, rhPostes) }))
    .map((x) => ({ ...x, restant: x.etapes.filter((e) => !e.fait && !e.facultative).length }))
    .sort((a, b) => b.restant - a.restant || a.p.nom.localeCompare(b.p.nom));

  const incomplets = gens.filter((x) => x.restant > 0);
  const vus = tout ? gens : incomplets;

  return (
    <section>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, maxWidth: 820, lineHeight: 1.55, margin: '0 0 14px' }}>
        De l’arrivée à la prise de poste. Le parcours ne concerne pas que les nouveaux : une étape
        manquante chez quelqu’un d’ancien est exactement le même trou. La dernière étape se vérifie
        contre les postes réellement déclarés — une habilitation qui ne correspond à aucun poste ne
        rend personne planifiable. <RefISO code="6.2" />
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <Segment ariaLabel="Effectif affiché" valeur={tout ? 'tous' : 'incomplets'} onChange={(v) => setTout(v === 'tous')}
          options={[
            { valeur: 'incomplets' as const, libelle: `À compléter (${incomplets.length})` },
            { valeur: 'tous' as const, libelle: `Tout l’effectif (${gens.length})` },
          ]} />
        <span style={{ flex: 1 }} />
        <Segment ariaLabel="Présentation" valeur={vue} onChange={setVue}
          options={[{ valeur: 'tableau' as const, libelle: 'Tableau' }, { valeur: 'cartes' as const, libelle: 'Cartes' }]} />
      </div>

      {!vus.length ? (
        attend ? <Skeleton rows={3} height={48} /> : <EmptyState titre="Aucun parcours à compléter"
          indice="Chaque personne de l’effectif a son badge, son contrat, ses habilitations et peut tenir au moins un poste." />
      ) : vue === 'tableau' ? (
        <TableauParcours lignes={vus} integrations={rhIntegrations} orgFonctions={orgFonctions} onAllerHabilitations={onAllerHabilitations}
          onOuvrir={setDossierOuvert}
          onCreer={async (orgId, dateEntree) => { await ouvrirIntegration(orgId, dateEntree || undefined); }} />
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {vus.map(({ p, etapes, restant }) => (
            <div key={p.orgId} style={{ ...card, padding: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>{nomDe(p)}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
                    {fonctionAffichee(p, orgFonctions)}
                    {p.dateEntree ? ` · entré le ${p.dateEntree}` : ''}
                  </div>
                </div>
                <span style={{
                  flex: 'none', fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-sm)', padding: '3px 9px',
                  background: restant ? 'var(--amber-50)' : 'var(--green-50)', color: restant ? 'var(--amber-600)' : 'var(--green-600)',
                }}>
                  {restant ? `${restant} étape(s) à compléter` : 'Parcours complet'}
                </span>
                {onAllerHabilitations && (
                  <Bouton taille="sm" onClick={() => onAllerHabilitations(p.orgId)}>
                    Habilitations
                  </Bouton>
                )}
                <BoutonDossier orgId={p.orgId}
                  dossier={rhIntegrations.find((d) => d.orgId === p.orgId)}
                  onOuvrir={setDossierOuvert}
                  onCreer={async () => {
                    if (await ouvrirIntegration(p.orgId, p.dateEntree || undefined)) {
                      // le dossier n'existe qu'après rechargement : on laisse
                      // l'utilisateur cliquer, plutôt que d'ouvrir un écran vide
                    }
                  }} />
              </div>

              <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 7 }}>
                {etapes.map((e) => (
                  <li key={e.cle} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{
                      flex: 'none', width: 18, height: 18, borderRadius: '50%', marginTop: 1,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      background: e.fait ? 'var(--green-50)' : e.facultative ? 'var(--gray-25)' : 'var(--amber-50)',
                      color: e.fait ? 'var(--green-600)' : e.facultative ? 'var(--text-muted)' : 'var(--amber-600)',
                    }}>
                      <I n={e.fait ? 'check' : 'info'} s={11} />
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600,
                        color: e.fait ? 'var(--text-strong)' : 'var(--text-muted)' }}>
                        {e.titre}
                        {e.facultative && !e.fait && <span style={{ fontWeight: 400 }}> — facultative</span>}
                      </span>
                      <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                        {e.detail}
                      </span>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

type LigneParcours = {
  p: { orgId: string; nom: string; fonction?: string | null; fonctionIds?: unknown[] | null; dateEntree?: string | null };
  etapes: EtapeParcours[];
  restant: number;
};

/**
 * LE TABLEAU : une ligne par personne, une colonne par point du parcours.
 *
 * Les colonnes sont celles des étapes de `parcoursDe` (jamais recopiées ici : une étape
 * ajoutée au parcours apparaît d'elle-même). Une case dit « fait », « à faire » ou
 * « facultatif non fait » par son icône ET par son texte alternatif — la couleur seule ne
 * se lit pas pour tout le monde — et le pourquoi complet est au survol et dans les cartes.
 * Le pied compte, par colonne, combien de personnes affichées ont franchi le point.
 */
function TableauParcours({ lignes, integrations, orgFonctions, onOuvrir, onCreer, onAllerHabilitations }: {
  lignes: LigneParcours[];
  orgFonctions: { id: unknown; label?: unknown }[];
  integrations: { id: string; orgId: string; statut: string; phases: { activites: { fait: boolean }[] }[] }[];
  onOuvrir: (id: string) => void;
  onCreer: (orgId: string, dateEntree?: string | null) => Promise<void>;
  onAllerHabilitations?: (orgId: string) => void;
}) {
  const colonnes = lignes[0]?.etapes || [];
  const titreDe = (cle: string) => cle === 'entree' ? 'Entrée' : cle === 'reste' ? 'Reste' : colonnes.find((c) => c.cle === cle)?.titre || cle;

  /* L5 (10/10/2026) : LE TABLEAU EST CELUI DU SOCLE. L'organisation des colonnes
     (afficher, masquer, ordonner — RQ, 03/10/2026) passe par son menu « Colonnes »,
     et la préférence vit EN BASE, par personne (l'ancienne clé locale du parcours
     est migrée au premier chargement). « Personne » est épinglée et obligatoire : un
     tableau sans le nom ne dit plus de qui l'on parle. Les colonnes viennent
     toujours des ÉTAPES du parcours, jamais recopiées. */
  const style: Record<EtatCellule, { bg: string; fg: string; icone: string }> = {
    fait: { bg: 'var(--green-50)', fg: 'var(--green-600)', icone: 'check' },
    manque: { bg: 'var(--amber-50)', fg: 'var(--amber-600)', icone: 'info' },
    facultatif: { bg: 'var(--gray-25)', fg: 'var(--text-muted)', icone: 'info' },
  };
  const etapeDe = (l: LigneParcours, cle: string) => l.etapes.find((e) => e.cle === cle);
  const celluleEtape = (cle: string, l: LigneParcours) => {
    const e = etapeDe(l, cle);
    if (!e) return null;
    const etat = etatCellule(e); const st = style[etat];
    return (
      <span role="img" aria-label={`${e.titre} : ${LIBELLE_CELLULE[etat]}. ${e.detail}`}
        title={`${e.titre} — ${LIBELLE_CELLULE[etat]}. ${e.detail}`}
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: st.bg, color: st.fg }}>
        <I n={st.icone} s={12} />
      </span>
    );
  };
  const centre: React.CSSProperties = { textAlign: 'center' };

  return (
    <div>
      <div style={{ ...card, padding: 0 }}>
        <Tableau
          id="rh-parcours"
          niveau="standard"
          defilementHorizontal
          lignes={lignes}
          cleLigne={(l) => l.p.orgId}
          vide={{ titre: 'Aucune personne dans le parcours.' }}
          colonnes={[
            {
              cle: 'personne', libelle: 'Personne', epinglee: true, obligatoire: true,
              valeur: (l) => nomDe(l.p), style: { fontWeight: 600, minWidth: 170 },
              rendu: (l) => (
                <>
                  <span style={{ color: 'var(--text-strong)' }}>{nomDe(l.p)}</span>
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 400, color: 'var(--text-muted)' }}>{fonctionAffichee(l.p, orgFonctions)}</span>
                </>
              ),
            },
            {
              cle: 'entree', libelle: 'Entrée', type: 'date', valeur: (l) => l.p.dateEntree || '',
              style: { ...centre, color: 'var(--text-muted)' },
              rendu: (l) => (l.p.dateEntree ? fmtDate(l.p.dateEntree) : '—'),
            },
            ...colonnes.map((c) => ({
              cle: c.cle, titre: c.titre,
              libelle: <>{c.titre}{c.facultative ? <span style={{ fontWeight: 400 }}> *</span> : null}</>,
              aide: c.facultative ? `${c.titre} — facultatif` : c.titre,
              /* 1 = franchi, 0 = à faire : trier la colonne range les personnes qui l'ont franchie. */
              valeur: (l: LigneParcours) => (etapeDe(l, c.cle)?.fait ? 1 : 0),
              libelleValeur: (v: string) => (v === '1' ? 'franchi' : 'à faire'),
              type: 'liste' as const,
              style: { ...centre, minWidth: 74, maxWidth: 104 },
              rendu: (l: LigneParcours) => celluleEtape(c.cle, l),
            })),
            {
              cle: 'reste', libelle: 'Reste', type: 'nombre', valeur: (l) => l.restant, style: centre,
              rendu: (l) => (
                <span style={{ fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-sm)', padding: '2px 8px',
                  background: l.restant ? 'var(--amber-50)' : 'var(--green-50)', color: l.restant ? 'var(--amber-600)' : 'var(--green-600)' }}>
                  {l.restant || 'Complet'}
                </span>
              ),
            },
          ]}
          /* Le dossier et les habilitations sont des ACTIONS : dernière colonne (É-9). */
          actions={(l) => (
            <span style={{ display: 'inline-grid', gap: 4, justifyItems: 'stretch' }}>
              {onAllerHabilitations && <Bouton taille="sm" onClick={() => onAllerHabilitations(l.p.orgId)}>Habilitations</Bouton>}
              <BoutonDossier orgId={l.p.orgId} dossier={integrations.find((d) => d.orgId === l.p.orgId)}
                onOuvrir={onOuvrir} onCreer={() => void onCreer(l.p.orgId, l.p.dateEntree)} />
            </span>
          )}
          /* Le pied compte, par point, les personnes AFFICHÉES qui l'ont franchi. */
          pied={(c, parmi) => (c.cle === 'personne'
            ? `Franchi par (${parmi.length} personne${parmi.length > 1 ? 's' : ''})`
            : colonnes.some((x) => x.cle === c.cle) ? `${parmi.filter((l) => etapeDe(l, c.cle)?.fait).length}/${parmi.length}` : '')}
        />
        <p style={{ margin: 0, padding: '8px 14px 12px', fontSize: 12, color: 'var(--text-muted)' }}>
          <span aria-hidden>✓</span> fait · <span aria-hidden>ⓘ</span> à faire · * point facultatif. Survolez une case pour lire le détail ; la présentation « Cartes » l’écrit en toutes lettres.
          {' '}Le menu « Colonnes » du tableau choisit ce qui s’affiche et dans quel ordre ({titreDe('reste')} et les points compris).
        </p>
      </div>
    </div>
  );
}

/** Bouton contextuel : ouvrir le dossier existant, ou en créer un. */
function BoutonDossier({ dossier, onOuvrir, onCreer }: {
  orgId: string;
  dossier?: { id: string; statut: string; phases: { activites: { fait: boolean }[] }[] };
  onOuvrir: (id: string) => void;
  onCreer: () => void;
}) {
  if (!dossier) {
    return <Bouton taille="sm" onClick={onCreer}>Ouvrir un dossier d’intégration</Bouton>;
  }
  const total = dossier.phases.reduce((n, p) => n + p.activites.length, 0);
  const faites = dossier.phases.reduce((n, p) => n + p.activites.filter((a) => a.fait).length, 0);
  return (
    <Bouton variante="primaire" taille="sm" onClick={() => onOuvrir(dossier.id)}>
      Dossier {dossier.statut === 'cloture' ? '(clôturé)' : `${faites}/${total}`}
    </Bouton>
  );
}
